/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useMemo, useState } from 'react';

import user from 'js/lib/user';

import { Button, Tag, Typography2 } from '@coursera/cds-core';
import type { Typography2Variant } from '@coursera/cds-core';
import type { SectionName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import useIsMobile from 'bundles/common/hooks/useIsMobile';
import TrackedButton from 'bundles/page/components/TrackedButton';
import SkillsTag from 'bundles/unified-description-page-common/components/modules/product-overview/SkillsTag';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';

// eslint-disable-next-line @coursera-web/custom-rules/i18n-import-rule
import _t from 'i18n!nls/xdp';

const CHARACTER_LIMIT = {
  DESKTOP: 180,
  MOBILE: 120,
};

const styles = {
  container: (enableCircularTagStyling: boolean) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${enableCircularTagStyling ? 'var(--cds-spacing-150) var(--cds-spacing-100);' : 'var(--cds-spacing-200);'};
    ${enableCircularTagStyling ? 'margin-top: var(--cds-spacing-150);' : ''};
  `,
  button: (enableCircularTagStyling: boolean) => css`
    padding: 0;
    margin-left: 0;
    ${enableCircularTagStyling ? 'text-decoration: underline;' : ''};

    :hover {
      background: transparent;
    }
  `,
  hideSkill: css`
    display: none;
  `,

  circularTagStylingOverrides: css`
    padding: 0;
    border-radius: 0;

    span {
      padding: var(--cds-spacing-50) var(--cds-spacing-150);
      border-radius: var(--cds-border-radius-max);
    }
  `,
};

export const getLastIndex = (skills: string[] | null | undefined, characterLimit: number) => {
  if (!skills?.length) {
    return -1;
  }

  let remainingCharacterCount = characterLimit;

  const index = skills.findIndex((skill) => {
    remainingCharacterCount -= skill.length;
    return remainingCharacterCount < 0;
  });

  if (index === -1) {
    return skills.length - 1;
  } else if (index === 0) {
    return index;
  }

  return index - 1;
};

type Props = {
  title?: string;
  titleTypographyVariant?: Typography2Variant;
  skills?: string[] | null;
  disableCTA?: boolean;
  enableSkillsLinkingOverride?: boolean;
  enableCircularTagStyling?: boolean;
  viewAllButtonText?: string;
  viewLessButtonText?: string;
  sectionName?: SectionName;
};

const Skills = ({
  skills,
  title,
  titleTypographyVariant,
  disableCTA,
  enableSkillsLinkingOverride,
  enableCircularTagStyling,
  viewAllButtonText,
  viewLessButtonText,
  sectionName,
}: Props) => {
  const [isShowingAll, setIsShowingAll] = useState(false);
  const { subfolderLocaleCode } = usePageInfo();
  const isMobile = useIsMobile();

  const track = useTracker();

  const toggleSkills = useCallback<() => void>(() => {
    if (!isShowingAll) {
      track('click_button', {
        button: { name: 'show_more_related_skills' },
        pageSection: { sectionName: sectionName ?? 'about' },
      });
    }
    setIsShowingAll((previousState) => !previousState);
  }, [track, isShowingAll, sectionName]);

  const characterLimit = isMobile ? CHARACTER_LIMIT.MOBILE : CHARACTER_LIMIT.DESKTOP;

  const viewAllButtonTextOrDefault = viewAllButtonText ?? _t('View all skills');
  const viewLessButtonTextOrDefault = viewLessButtonText ?? _t('View less skills');

  const lastIndexToShow = useMemo(() => {
    return getLastIndex(skills, characterLimit);
  }, [skills, characterLimit]);

  if (!skills?.length) {
    return null;
  }

  // Add links for each skill only if logged out
  const enableSkillsLinking =
    enableSkillsLinkingOverride || (!user.isAuthenticatedUser() && !disableCTA && !subfolderLocaleCode);

  return (
    <Subsection
      title={
        <Typography2 component="h2" variant={titleTypographyVariant ?? 'subtitleLarge'}>
          {title ?? _t("Skills you'll gain")}
        </Typography2>
      }
      spacing={24}
    >
      <ul css={styles.container(!!enableCircularTagStyling)}>
        {skills.map((skill, index) => {
          return (
            <li key={skill} css={!isShowingAll && index > lastIndexToShow ? styles.hideSkill : undefined}>
              {enableSkillsLinking ? (
                <SkillsTag
                  skill={skill}
                  cssOverrides={enableCircularTagStyling ? styles.circularTagStylingOverrides : undefined}
                  sectionName={sectionName}
                />
              ) : (
                <Tag>{skill}</Tag>
              )}
            </li>
          );
        })}
        {!disableCTA && skills.length - 1 > lastIndexToShow && (
          <li>
            <Button
              variant="ghost"
              edgeAlign="start"
              size="small"
              onClick={toggleSkills}
              css={styles.button(!!enableCircularTagStyling)}
              component={TrackedButton}
              trackingName="overview_skills_toggle"
              requireFullyVisible={false}
              withVisibilityTracking={false}
            >
              {isShowingAll ? viewLessButtonTextOrDefault : viewAllButtonTextOrDefault}
            </Button>
          </li>
        )}
      </ul>
    </Subsection>
  );
};

export default Skills;
